/**
 * AlphaX ECU Staff Diagnostic & Warranty Ticketing System
 * Interactive Frontend Controller & State Manager
 */

// 1. Paste your deployed Google Apps Script Web App URL here:
const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyBf9lBmTqYF8r24_GPlEAWy3cgvs3IqyZ0JZ19vRvmP2Azm7Odq5ceGoDAZkGwJg5_Zw/exec';

(function () {
  'use strict';

  // Default AlphaX Roles & Registered User Accounts
  const DEFAULT_ROLES = [
    "CEO",
    "Design Engineer",
    "Certified Tuner",
    "Diagnostic & Shipping",
    "Customer Support"
  ];


  // Helper: Compute SHA-256 Password Hash
  async function sha256(message) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Helper: Convert Google Drive URLs to Direct Raw Thumbnail Images
  function getDirectImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('data:image')) return url;
    if (url.includes('drive.google.com/thumbnail')) return url;

    let fileId = '';
    const match1 = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    const match2 = url.match(/id=([a-zA-Z0-9_-]+)/);
    if (match1 && match1[1]) fileId = match1[1];
    else if (match2 && match2[1]) fileId = match2[1];

    if (fileId) {
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
    }
    return url;
  }


  // Helper: Post payload to Google Apps Script Web App if configured
  async function apiPost(payload) {
    if (!APPS_SCRIPT_URL || APPS_SCRIPT_URL.includes('YOUR_DEPLOYED_APPS_SCRIPT')) {
      return null; // Local storage fallback mode
    }
    try {
      const resp = await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      }).catch(e => {
        console.warn("Google Apps Script network fetch bypassed:", e);
        return null;
      });

      if (!resp || !resp.ok) return null;
      return await resp.json().catch(() => null);
    } catch (err) {
      console.warn("Apps Script API notice:", err);
      return null;
    }
  }


  // Encrypted default password hash for "mipassword123": 78b31c2f705d051930bc67198c290c9b1eb92f1379d2a932bcaa85c258d64d77
  const DEFAULT_PASSWORD_HASH = "78b31c2f705d051930bc67198c290c9b1eb92f1379d2a932bcaa85c258d64d77";

  const SEED_STAFF = [
    { name: "Norman Paulino", email: "normsp@gmail.com", role: "Design Engineer", passwordHash: DEFAULT_PASSWORD_HASH, avatar: "NP" },
    { name: "Raymond Ortiz", email: "ankyth@gmail.com", role: "Diagnostic & Shipping", passwordHash: DEFAULT_PASSWORD_HASH, avatar: "RO" },
    { name: "Jose Perez Betancourt", email: "jrpbcc@gmail.com", role: "CEO", passwordHash: DEFAULT_PASSWORD_HASH, avatar: "JP" },
    { name: "Richard Figueroa", email: "Richardfigue1218@gmail.com", role: "Customer Support", passwordHash: DEFAULT_PASSWORD_HASH, avatar: "RF" },
    { name: "Jaime Rodriguez", email: "jrc.jaime@gmail.com", role: "Certified Tuner", passwordHash: DEFAULT_PASSWORD_HASH, avatar: "JR" }
  ];

  // Seed sample tickets for initial database population
  const SEED_TICKETS = [
    {
      id: "AX-TICK-10042",
      createdAt: "2026-08-04T14:30:00Z",
      updatedAt: "2026-08-04T18:15:00Z",
      client: {
        name: "Marcus Vance",
        orderId: "#AX-98421",
        serialNumber: "ECU-V8-77492-X",
        vehicle: "2023 Audi RS5 2.9T"
      },
      classification: "tuning",
      status: "diagnosing",
      priority: "high",
      assignedStaff: {
        name: "Jaime Rodriguez",
        role: "Certified Tuner",
        avatar: "JR"
      },
      assignmentHistory: [
        {
          assignedFrom: "Richard Figueroa (Customer Support)",
          assignedTo: "Jaime Rodriguez (Certified Tuner)",
          timestamp: "2026-08-04T15:00:00Z",
          reason: "Customer claim intake complete. Requires ECU calibration datalog review."
        }
      ],
      shipping: {
        incoming: { carrier: "UPS", trackingNumber: "1Z9999999999999999", status: "Received" },
        outgoing: { carrier: "FedEx Express", trackingNumber: "", status: "Pending" }
      },
      attachments: [
        { id: "img_1", name: "ecu_bench_pinout.jpg", url: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=400&q=80" },
        { id: "img_2", name: "datalog_graph.png", url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=400&q=80" }
      ],
      comments: [
        { id: "c1", author: "Richard Figueroa (Customer Support)", text: "Package received at warehouse. Outer box clean, ECU serial matches #AX-98421.", timestamp: "2026-08-04T14:45:00Z" },
        { id: "c2", author: "Jaime Rodriguez (Certified Tuner)", text: "Inspected flash datalog. High rpm knock retard due to fuel octane map selection, hardware circuits are intact.", timestamp: "2026-08-04T18:15:00Z" }
      ]
    },
    {
      id: "AX-TICK-10043",
      createdAt: "2026-07-29T11:20:00Z", // ~8 days old (linear partial glow)
      updatedAt: "2026-08-04T16:40:00Z",
      client: {
        name: "David Miller",
        orderId: "#AX-97210",
        serialNumber: "ECU-S58-90412",
        vehicle: "2022 BMW M3 Competition"
      },
      classification: "hardware",
      status: "hardware_repair",
      priority: "urgent",
      assignedStaff: {
        name: "Norman Paulino",
        role: "Design Engineer",
        avatar: "NP"
      },
      newAssignmentFor: "Norman Paulino",
      assignmentHistory: [
        {
          assignedFrom: "Jaime Rodriguez (Certified Tuner)",
          assignedTo: "Norman Paulino (Design Engineer)",
          timestamp: "2026-08-04T10:00:00Z",
          reason: "Tuning maps verified clean. Injector driver MOSFET Q4 overheating on PCB."
        }
      ],
      shipping: {
        incoming: { carrier: "FedEx", trackingNumber: "781299882233", status: "Received" },
        outgoing: { carrier: "FedEx", trackingNumber: "789012345678", status: "Label Created" }
      },
      attachments: [
        { id: "img_3", name: "blown_mosfet_bench.jpg", url: "https://images.unsplash.com/photo-1597733336794-12d05021d510?auto=format&fit=crop&w=400&q=80" }
      ],
      comments: [
        { id: "c3", author: "Norman Paulino (Design Engineer)", text: "Replaced damaged MOSFET Q4 on main driver rail. Bench tested power output stable at 14.2V.", timestamp: "2026-08-04T16:40:00Z" }
      ]
    },
    {
      id: "AX-TICK-10044",
      createdAt: "2026-07-21T09:10:00Z", // ~16 days old (max glow >= 14 days)
      updatedAt: "2026-08-04T09:10:00Z",
      client: {
        name: "Elena Rostova",
        orderId: "#AX-10112",
        serialNumber: "ECU-EA888-44912",
        vehicle: "2021 VW Golf R 2.0T"
      },
      classification: "diagnosing",
      status: "received",
      priority: "normal",
      assignedStaff: {
        name: "Raymond Ortiz",
        role: "Diagnostic & Shipping",
        avatar: "RO"
      },
      assignmentHistory: [],
      shipping: {
        incoming: { carrier: "USPS Priority", trackingNumber: "940011189956", status: "Received" },
        outgoing: { carrier: "", trackingNumber: "", status: "Pending" }
      },
      attachments: [],
      comments: [
        { id: "c4", author: "Raymond Ortiz (Diagnostic & Shipping)", text: "Logged incoming package. Awaiting bench diagnostic assignment.", timestamp: "2026-08-04T09:15:00Z" }
      ]
    }
  ];



  class AlphaXApp {
    constructor() {
      this.roles = this.loadRoles();
      this.staffList = this.loadStaff();
      this.tickets = this.loadTickets();
      this.currentUser = this.loadSession();
      this.activeTicketId = null;
      this.initElements();
      this.bindEvents();
      this.checkSession();
      this.render();
      this.syncLiveTickets(); // Fetch live tickets directly from Google Sheets
    }

    async syncLiveTickets() {
      if (!APPS_SCRIPT_URL || APPS_SCRIPT_URL.includes('YOUR_DEPLOYED_APPS_SCRIPT')) return;
      try {
        const resp = await fetch(APPS_SCRIPT_URL).catch(() => null);
        if (!resp || !resp.ok) return;
        const data = await resp.json().catch(() => null);

        if (data && data.success && Array.isArray(data.tickets) && data.tickets.length > 0) {
          this.tickets = data.tickets;
          this.saveTickets();
          this.render();
        }
      } catch (err) {
        console.warn("Live Google Sheets ticket sync notice:", err);
      }
    }


    loadSession() {
      const stored = sessionStorage.getItem('alphax_active_user');
      if (stored) {
        try { return JSON.parse(stored); } catch (e) {}
      }
      return null;
    }

    saveSession(user) {
      this.currentUser = user;
      if (user) {
        sessionStorage.setItem('alphax_active_user', JSON.stringify(user));
      } else {
        sessionStorage.removeItem('alphax_active_user');
      }
    }

    showToast(titleText, detailMsg, ticketId = null) {
      if (!this.toastContainer) {
        this.toastContainer = document.getElementById('alphax-toast-container');
      }
      if (!this.toastContainer) {
        const cont = document.createElement('div');
        cont.id = 'alphax-toast-container';
        cont.className = 'alphax-toast-container';
        document.body.appendChild(cont);
        this.toastContainer = cont;
      }

      const toastEl = document.createElement('div');
      toastEl.className = 'alphax-toast';
      toastEl.innerHTML = `
        <div class="alphax-toast-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        </div>
        <div class="alphax-toast-content">
          <div class="alphax-toast-title">${titleText || "you have a ticket update"}</div>
          <div class="alphax-toast-message">${detailMsg || "A ticket update requires your attention."}</div>
        </div>
        <button class="alphax-toast-close" title="Dismiss">&times;</button>
      `;

      const closeBtn = toastEl.querySelector('.alphax-toast-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          toastEl.classList.add('toast-hiding');
          setTimeout(() => toastEl.remove(), 300);
        });
      }

      if (ticketId) {
        toastEl.addEventListener('click', () => {
          this.openDrawer(ticketId);
          toastEl.classList.add('toast-hiding');
          setTimeout(() => toastEl.remove(), 300);
        });
      }

      this.toastContainer.appendChild(toastEl);

      setTimeout(() => {
        if (toastEl.parentNode) {
          toastEl.classList.add('toast-hiding');
          setTimeout(() => toastEl.remove(), 300);
        }
      }, 7000);
    }

    checkSession() {
      if (this.currentUser) {
        if (this.loginBackdrop) this.loginBackdrop.classList.remove('active');
        this.updateUserBadge();

        // Check if there are tickets assigned to this staff member with unread updates
        const unreadForUser = this.tickets.filter(t => 
          t.newAssignmentFor && t.newAssignmentFor.toLowerCase() === this.currentUser.name.toLowerCase()
        );
        if (unreadForUser.length > 0) {
          const ids = unreadForUser.map(t => t.id).join(', ');
          this.showToast("you have a ticket update", `You have ${unreadForUser.length} newly assigned ticket(s): ${ids}`, unreadForUser[0].id);
        }
      } else {
        if (this.loginBackdrop) this.loginBackdrop.classList.add('active');
      }
    }

    updateUserBadge() {
      const avatarEl = document.getElementById('current-user-avatar');
      const nameEl = document.getElementById('current-user-name');
      const roleEl = document.getElementById('current-user-role');
      const btnLogout = document.getElementById('btn-staff-logout');
      const btnOpenCP = document.getElementById('btn-open-change-password');
      const userDisplay = document.getElementById('current-user-display');

      if (this.currentUser) {
        if (avatarEl) avatarEl.textContent = this.currentUser.avatar;
        if (nameEl) nameEl.textContent = this.currentUser.name;
        if (roleEl) roleEl.textContent = this.currentUser.role;
        if (userDisplay) userDisplay.style.display = 'flex';
        if (btnLogout) btnLogout.style.display = 'inline-block';
        if (btnOpenCP) btnOpenCP.style.display = 'inline-block';
      } else {
        if (avatarEl) avatarEl.textContent = '?';
        if (nameEl) nameEl.textContent = 'Not Authenticated';
        if (roleEl) roleEl.textContent = 'Staff Login Required';
        if (btnLogout) btnLogout.style.display = 'none';
        if (btnOpenCP) btnOpenCP.style.display = 'none';
      }
    }




    loadRoles() {
      const stored = localStorage.getItem('alphax_roles');
      if (stored) {
        try { return JSON.parse(stored); } catch (e) {}
      }
      localStorage.setItem('alphax_roles', JSON.stringify(DEFAULT_ROLES));
      return DEFAULT_ROLES;
    }

    saveRoles() {
      localStorage.setItem('alphax_roles', JSON.stringify(this.roles));
    }

    loadStaff() {
      const stored = localStorage.getItem('alphax_staff');
      if (stored) {
        try { return JSON.parse(stored); } catch (e) {}
      }
      localStorage.setItem('alphax_staff', JSON.stringify(SEED_STAFF));
      return SEED_STAFF;
    }

    saveStaff() {
      localStorage.setItem('alphax_staff', JSON.stringify(this.staffList));
    }

    loadTickets() {
      const stored = localStorage.getItem('alphax_tickets');
      if (stored) {
        try { return JSON.parse(stored); } catch (e) { console.error('Failed to parse tickets', e); }
      }
      localStorage.setItem('alphax_tickets', JSON.stringify(SEED_TICKETS));
      return SEED_TICKETS;
    }

    saveTickets() {
      localStorage.setItem('alphax_tickets', JSON.stringify(this.tickets));
    }

    initElements() {
      this.gridContainer = document.getElementById('ticket-grid-container');
      this.searchInput = document.getElementById('ticket-search-input');
      this.filterClassification = document.getElementById('filter-classification');
      this.filterStatus = document.getElementById('filter-status');
      this.filterStaff = document.getElementById('filter-staff');

      // Login Modal & Profile
      this.loginBackdrop = document.getElementById('login-modal-backdrop');
      this.formLogin = document.getElementById('form-staff-login');
      this.loginErrorMsg = document.getElementById('login-error-msg');
      this.btnLogout = document.getElementById('btn-staff-logout');

      // Change Password Modal
      this.cpBackdrop = document.getElementById('change-password-modal-backdrop');
      this.btnOpenCP = document.getElementById('btn-open-change-password');
      this.btnCloseCP = document.getElementById('btn-close-change-password');
      this.btnCancelCP = document.getElementById('btn-cancel-change-password');
      this.formCP = document.getElementById('form-change-password');
      this.cpErrorMsg = document.getElementById('cp-error-msg');
      this.cpSuccessMsg = document.getElementById('cp-success-msg');

      // Stats
      this.statTotal = document.getElementById('stat-total');
      this.statTuning = document.getElementById('stat-tuning');
      this.statHardware = document.getElementById('stat-hardware');
      this.statShipping = document.getElementById('stat-shipping');

      // Drawer
      this.drawerBackdrop = document.getElementById('ticket-detail-backdrop');
      this.drawerHeader = document.getElementById('drawer-header-content');
      this.drawerBody = document.getElementById('drawer-body-content');
      this.btnCloseDrawer = document.getElementById('btn-close-drawer');

      // Create Ticket Modal
      this.createBackdrop = document.getElementById('create-ticket-backdrop');
      this.btnOpenCreate = document.getElementById('btn-open-create-modal');
      this.btnCloseCreate = document.getElementById('btn-close-create-modal');
      this.btnCancelCreate = document.getElementById('btn-cancel-create');
      this.formCreate = document.getElementById('form-create-ticket');

      // Export button
      this.btnExport = document.getElementById('btn-export-tickets');

      // Toast container
      this.toastContainer = document.getElementById('alphax-toast-container');

      // Public Claim Form
      this.publicClaimForm = document.getElementById('public-claim-form');
    }

    bindEvents() {
      if (this.formLogin) {
        this.formLogin.addEventListener('submit', async (e) => {
          e.preventDefault();
          const emailInput = document.getElementById('login-email').value.trim().toLowerCase();
          const passInput = document.getElementById('login-password').value;

          const hashed = await sha256(passInput);

          // 1. Send authentication request to Google Apps Script (Verifies password against Google Sheets)
          const remoteRes = await apiPost({
            action: 'login',
            email: emailInput,
            password: passInput
          });

          let staffUser = null;

          if (remoteRes) {
            // Network request reached Google Sheets
            if (remoteRes.success && remoteRes.user) {
              staffUser = {
                name: remoteRes.user.name,
                email: remoteRes.user.email,
                role: remoteRes.user.role,
                avatar: remoteRes.user.avatar || remoteRes.user.name.split(' ').map(n => n[0]).join('')
              };
            } else {
              // Google Sheets rejected the login (Invalid password or account not found)
              if (this.loginErrorMsg) {
                this.loginErrorMsg.textContent = remoteRes.message || 'Invalid email address or password.';
                this.loginErrorMsg.style.display = 'block';
              }
              return;
            }
          } else {
            // Local fallback mode if Google Apps Script is unconfigured or offline
            const localStaff = this.staffList.find(s => s.email.toLowerCase() === emailInput);
            if (localStaff && localStaff.passwordHash === hashed) {
              staffUser = localStaff;
            } else {
              if (this.loginErrorMsg) {
                this.loginErrorMsg.textContent = 'Invalid email address or password.';
                this.loginErrorMsg.style.display = 'block';
              }
              return;
            }
          }


          // Authenticated
          if (this.loginErrorMsg) this.loginErrorMsg.style.display = 'none';
          this.saveSession(staffUser);
          this.checkSession();
        });
      }


      if (this.btnLogout) {
        this.btnLogout.addEventListener('click', () => {
          this.saveSession(null);
          this.checkSession();
        });
      }

      // Change Password Events
      if (this.btnOpenCP) {
        this.btnOpenCP.addEventListener('click', () => {
          if (this.cpBackdrop) this.cpBackdrop.classList.add('active');
        });
      }
      if (this.btnCloseCP) {
        this.btnCloseCP.addEventListener('click', () => this.closeCPModal());
      }
      if (this.btnCancelCP) {
        this.btnCancelCP.addEventListener('click', () => this.closeCPModal());
      }
      if (this.formCP) {
        this.formCP.addEventListener('submit', async (e) => {
          e.preventDefault();
          const currPass = document.getElementById('cp-current-password').value;
          const newPass = document.getElementById('cp-new-password').value;
          const confPass = document.getElementById('cp-confirm-password').value;

          if (!this.currentUser) return;

          const currHash = await sha256(currPass);
          const staffObj = this.staffList.find(s => s.email.toLowerCase() === this.currentUser.email.toLowerCase());

          if (!staffObj || staffObj.passwordHash !== currHash) {
            if (this.cpErrorMsg) {
              this.cpErrorMsg.textContent = 'Current password is incorrect.';
              this.cpErrorMsg.style.display = 'block';
            }
            if (this.cpSuccessMsg) this.cpSuccessMsg.style.display = 'none';
            return;
          }

          if (newPass !== confPass) {
            if (this.cpErrorMsg) {
              this.cpErrorMsg.textContent = 'New password and confirmation password do not match.';
              this.cpErrorMsg.style.display = 'block';
            }
            if (this.cpSuccessMsg) this.cpSuccessMsg.style.display = 'none';
            return;
          }

          const newHash = await sha256(newPass);
          staffObj.passwordHash = newHash;
          this.currentUser.passwordHash = newHash;
          this.saveStaff();
          this.saveSession(this.currentUser);

          // Remote Sync to Google Sheets ('Users' tab Column D EncryptedPassword)
          apiPost({
            action: 'changePassword',
            email: this.currentUser.email,
            newPassword: newPass
          });

          if (this.cpErrorMsg) this.cpErrorMsg.style.display = 'none';
          if (this.cpSuccessMsg) this.cpSuccessMsg.style.display = 'block';


          setTimeout(() => {
            this.closeCPModal();
            this.formCP.reset();
            if (this.cpSuccessMsg) this.cpSuccessMsg.style.display = 'none';
          }, 1500);
        });
      }

      if (this.searchInput) this.searchInput.addEventListener('input', () => this.renderGrid());
      if (this.filterClassification) this.filterClassification.addEventListener('change', () => this.renderGrid());
      if (this.filterStatus) this.filterStatus.addEventListener('change', () => this.renderGrid());
      if (this.filterStaff) this.filterStaff.addEventListener('change', () => this.renderGrid());


      if (this.btnCloseDrawer) {
        this.btnCloseDrawer.addEventListener('click', () => this.closeDrawer());
      }
      if (this.drawerBackdrop) {
        this.drawerBackdrop.addEventListener('click', (e) => {
          if (e.target === this.drawerBackdrop) this.closeDrawer();
        });
      }

      if (this.btnOpenCreate) {
        this.btnOpenCreate.addEventListener('click', () => this.openCreateModal());
      }
      if (this.btnCloseCreate) {
        this.btnCloseCreate.addEventListener('click', () => this.closeCreateModal());
      }
      if (this.btnCancelCreate) {
        this.btnCancelCreate.addEventListener('click', () => this.closeCreateModal());
      }
      if (this.formCreate) {
        this.formCreate.addEventListener('submit', (e) => this.handleCreateTicket(e));
      }
      if (this.btnExport) {
        this.btnExport.addEventListener('click', () => this.exportTicketsJSON());
      }

      if (this.publicClaimForm) {
        this.publicClaimForm.addEventListener('submit', (e) => this.handlePublicClaim(e));
      }
    }

    render() {
      this.updateStats();
      this.renderGrid();
    }

    updateStats() {
      if (!this.statTotal) return;
      // Filter out closed/archived tickets from active metrics
      const activeTickets = this.tickets.filter(t => t.status !== 'closed');

      const total = activeTickets.length;
      const tuning = activeTickets.filter(t => t.classification === 'tuning').length;
      const hardware = activeTickets.filter(t => t.classification === 'hardware').length;
      const shipping = activeTickets.filter(t => t.shipping && t.shipping.outgoing && t.shipping.outgoing.status !== 'Shipped').length;

      this.statTotal.textContent = total;
      this.statTuning.textContent = tuning;
      this.statHardware.textContent = hardware;
      this.statShipping.textContent = shipping;
    }


    getFilteredTickets() {
      const search = (this.searchInput ? this.searchInput.value : '').toLowerCase().trim();
      const catFilter = this.filterClassification ? this.filterClassification.value : 'all';
      const statusFilter = this.filterStatus ? this.filterStatus.value : 'all';
      const staffFilter = this.filterStaff ? this.filterStaff.value : 'all';

      return this.tickets.filter(t => {
        const matchesSearch = !search ||
          t.id.toLowerCase().includes(search) ||
          t.client.name.toLowerCase().includes(search) ||
          t.client.orderId.toLowerCase().includes(search) ||
          t.client.serialNumber.toLowerCase().includes(search) ||
          (t.assignedStaff && t.assignedStaff.name.toLowerCase().includes(search));

        const matchesCat = catFilter === 'all' || t.classification === catFilter;

        let matchesStatus = true;
        if (statusFilter === 'all') {
          matchesStatus = t.status !== 'closed'; // Exclude closed by default
        } else if (statusFilter === 'all_with_closed') {
          matchesStatus = true;
        } else {
          matchesStatus = t.status === statusFilter;
        }

        const matchesStaff = staffFilter === 'all' || (t.assignedStaff && t.assignedStaff.name === staffFilter);

        return matchesSearch && matchesCat && matchesStatus && matchesStaff;
      });
    }


    getTicketAgeInfo(createdAt) {
      if (!createdAt) return { ageInDays: 0, daysFloor: 0, glowRatio: 0, labelText: "0 days old" };

      const createdTime = new Date(createdAt).getTime();
      const now = Date.now();
      const diffMs = Math.max(0, now - createdTime);
      const ageInDays = diffMs / (1000 * 60 * 60 * 24);
      const daysFloor = Math.floor(ageInDays);

      let glowRatio = 0;
      if (ageInDays <= 3) {
        glowRatio = 0;
      } else if (ageInDays >= 14) {
        glowRatio = 1;
      } else {
        glowRatio = (ageInDays - 3) / 11;
      }

      const labelText = daysFloor === 1 ? '1 day old' : `${daysFloor} days old`;

      return {
        ageInDays,
        daysFloor,
        glowRatio,
        labelText
      };
    }


    renderGrid() {
      if (!this.gridContainer) return;
      const filtered = this.getFilteredTickets();

      if (filtered.length === 0) {
        this.gridContainer.innerHTML = `
          <div class="alphax-loading-state">
            <p style="font-size:1.1rem; color:#94a3b8;">No AlphaX tickets match your filter criteria.</p>
          </div>
        `;
        return;
      }

      this.gridContainer.innerHTML = filtered.map(ticket => {
        const ageInfo = this.getTicketAgeInfo(ticket.createdAt);
        const glowRatio = ageInfo.glowRatio;

        // Determine if 1Hz blue or red-blue flipping glow should be active for the assigned staff member
        const isAssigned = ticket.assignedStaff && ticket.assignedStaff.name && ticket.assignedStaff.name !== 'Unassigned';
        const isNewAssignment = ticket.newAssignmentFor && isAssigned && 
          ticket.newAssignmentFor.toLowerCase() === ticket.assignedStaff.name.toLowerCase();
        
        // Active glow for the newly assigned staff member
        const isGlowingForUser = isNewAssignment && (
          !this.currentUser || this.currentUser.name.toLowerCase() === ticket.assignedStaff.name.toLowerCase()
        );

        let glowClass = '';
        if (isGlowingForUser) {
          if (glowRatio > 0) {
            glowClass = 'glow-1hz-red-blue'; // Flips between red and blue at 1Hz if time glow is active
          } else {
            glowClass = 'glow-1hz-blue'; // Glows blue at 1Hz if time glow is not active
          }
        }

        return `
          <div class="alphax-card card-${ticket.classification} ${glowClass}" data-id="${ticket.id}" style="--glow-ratio: ${glowRatio.toFixed(4)};">
            <div class="card-top">
              <div>
                <span class="ticket-id">${ticket.id}</span>
                ${isGlowingForUser ? '<span class="alphax-badge status-reassigned" style="margin-left:0.4rem;">⚡ REASSIGNED</span>' : ''}
                <div class="client-name">${ticket.client.name}</div>
              </div>
              ${this.renderBadge('classification', ticket.classification)}
            </div>

            <div class="card-metadata">
              <div class="meta-item">
                <span class="meta-label">Order Ref / ID:</span>
                <span class="meta-val">${ticket.client.orderId}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">${ticket.classification === 'sale' ? 'Product / Model:' : 'ECU Serial #:'}</span>
                <span class="meta-val">${ticket.client.serialNumber}</span>
              </div>
              ${ticket.client.vehicle ? `
              <div class="meta-item">
                <span class="meta-label">Vehicle Spec:</span>
                <span class="meta-val">${ticket.client.vehicle}</span>
              </div>` : ''}
            </div>

            <div class="card-footer">
              <div class="card-footer-left">
                <div class="assigned-staff-badge">
                  <span class="staff-avatar">${ticket.assignedStaff ? ticket.assignedStaff.avatar : 'UN'}</span>
                  <span>${ticket.assignedStaff ? ticket.assignedStaff.name : 'Unassigned'}</span>
                </div>
                <div class="ticket-age-label" title="Created on ${new Date(ticket.createdAt).toLocaleDateString()}">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>${ageInfo.labelText}</span>
                </div>
              </div>
              <div class="card-footer-right">
                ${this.renderBadge('status', ticket.status)}
              </div>
            </div>
          </div>
        `;
      }).join('');

      // Add click listeners to cards
      this.gridContainer.querySelectorAll('.alphax-card').forEach(card => {
        card.addEventListener('click', () => {
          const id = card.getAttribute('data-id');
          this.openDrawer(id);
        });
      });
    }

    renderBadge(type, value) {
      if (type === 'classification') {
        if (value === 'tuning') {
          return `<span class="alphax-badge status-tuning"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 20v-6M6 20V10M18 20V4"/></svg> Tuning Issue</span>`;
        } else if (value === 'hardware') {
          return `<span class="alphax-badge status-hardware"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> Hardware Fault</span>`;
        } else if (value === 'sale') {
          return `<span class="alphax-badge status-sale">🛒 New Direct Sale</span>`;
        }
        return `<span class="alphax-badge badge-unassigned">Diagnosing</span>`;
      }

      if (type === 'status') {
        switch (value) {
          case 'preparing_ecu':
            return `<span class="alphax-badge status-preparing">Preparing ECU</span>`;
          case 'qc_passed':
            return `<span class="alphax-badge status-qc">QC Passed</span>`;
          case 'ready_to_ship':
            return `<span class="alphax-badge status-ready">Ready to Ship</span>`;
          case 'shipped':
            return `<span class="alphax-badge status-shipped">Outbound Shipped</span>`;
          case 'delivered':
            return `<span class="alphax-badge status-delivered">Delivered</span>`;
          case 'received':
            return `<span class="alphax-badge status-received">RMA Received</span>`;
          case 'diagnosing':
            return `<span class="alphax-badge status-diagnosing">In Diagnosis</span>`;
          case 'tuning_fix':
            return `<span class="alphax-badge status-tuning">Tuning Calibrated</span>`;
          case 'hardware_repair':
            return `<span class="alphax-badge status-hardware">Hardware Repair</span>`;
          case 'resolved':
            return `<span class="alphax-badge status-resolved">Resolved</span>`;
          case 'closed':
            return `<span class="alphax-badge status-closed">Closed / Archived</span>`;
          default:
            return `<span class="alphax-badge status-default">${value.replace('_', ' ')}</span>`;
        }
      }

      if (type === 'status') {
        const labels = {
          received: 'RMA Received',
          diagnosing: 'In Diagnosis',
          tuning_fix: 'Tuning Calibrated',
          hardware_repair: 'Hardware Repair',
          shipped: 'Outbound Shipped',
          resolved: 'Resolved'
        };
        return `<span class="alphax-badge status-${value}">${labels[value] || value}</span>`;
      }
      return '';
    }

    openDrawer(ticketId) {
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) return;

      // Clear the unread assignment glow when ticket is inspected
      if (ticket.newAssignmentFor) {
        ticket.newAssignmentFor = null;
        this.saveTickets();
        this.renderGrid();
      }

      const ageInfo = this.getTicketAgeInfo(ticket.createdAt);

      this.activeTicketId = ticketId;
      this.drawerHeader.innerHTML = `
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <h2 style="margin:0; font-family:'Outfit',sans-serif; color:#fff;">${ticket.id}</h2>
          ${this.renderBadge('classification', ticket.classification)}
        </div>
        <p style="margin:0.4rem 0 0 0; color:#94a3b8; font-size:0.9rem;">
          Client: <strong>${ticket.client.name}</strong> | Order <strong>${ticket.client.orderId}</strong> | Serial <strong>${ticket.client.serialNumber}</strong> | Age: <strong>${ageInfo.labelText}</strong>
        </p>
      `;

      this.renderDrawerBody(ticket);
      this.drawerBackdrop.classList.add('active');
    }

    renderDrawerBody(ticket) {
      this.drawerBody.innerHTML = `
        <!-- Classification & Status Controls -->
        <div class="detail-section">
          <h4>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            Diagnostic & Workflow Status
          </h4>
          
          <label style="font-size:0.8rem; color:#94a3b8; display:block; margin-bottom:0.4rem;">Record & Classification Type:</label>
          <div class="classification-toggle-grid" style="margin-bottom:1rem; grid-template-columns: repeat(3, 1fr);">
            <div class="toggle-option ${ticket.classification === 'tuning' ? 'active-tuning' : ''}" id="toggle-tuning">
              ⚙️ Tuning Issue
            </div>
            <div class="toggle-option ${ticket.classification === 'hardware' ? 'active-hardware' : ''}" id="toggle-hardware">
              ⚡ Hardware Fault
            </div>
            <div class="toggle-option ${ticket.classification === 'sale' ? 'active-sale' : ''}" id="toggle-sale">
              🛒 Direct Sale
            </div>
          </div>

          <label style="font-size:0.8rem; color:#94a3b8; display:block; margin-bottom:0.4rem;">Ticket Lifecycle & Fulfillment State:</label>
          <div style="display:flex; gap:0.75rem; align-items:center;">
            <select id="select-ticket-status" class="alphax-select" style="flex:1;">
              <optgroup label="Warranty & Technical RMA Stages">
                <option value="received" ${ticket.status === 'received' ? 'selected' : ''}>RMA Received</option>
                <option value="diagnosing" ${ticket.status === 'diagnosing' ? 'selected' : ''}>In Diagnosis</option>
                <option value="tuning_fix" ${ticket.status === 'tuning_fix' ? 'selected' : ''}>Tuning Calibrated</option>
                <option value="hardware_repair" ${ticket.status === 'hardware_repair' ? 'selected' : ''}>Hardware Repair</option>
                <option value="resolved" ${ticket.status === 'resolved' ? 'selected' : ''}>Resolved</option>
              </optgroup>
              <optgroup label="Direct Sale Fulfillment Stages">
                <option value="preparing_ecu" ${ticket.status === 'preparing_ecu' ? 'selected' : ''}>Preparing ECU</option>
                <option value="qc_passed" ${ticket.status === 'qc_passed' ? 'selected' : ''}>QC Passed / Bench Tested</option>
                <option value="ready_to_ship" ${ticket.status === 'ready_to_ship' ? 'selected' : ''}>Ready to Ship</option>
                <option value="shipped" ${ticket.status === 'shipped' ? 'selected' : ''}>Outbound Shipped</option>
                <option value="delivered" ${ticket.status === 'delivered' ? 'selected' : ''}>Delivered</option>
              </optgroup>
              <optgroup label="Case Closure">
                <option value="closed" ${ticket.status === 'closed' ? 'selected' : ''}>Closed / Archived</option>
              </optgroup>
            </select>
            <button type="button" class="alphax-btn alphax-btn-secondary" id="btn-update-status">Save State</button>
          </div>



          <div style="margin-top:1rem; padding-top:1rem; border-top:1px solid rgba(255,255,255,0.06); display:flex; justify-content:space-between; align-items:center;">
            <span style="font-size:0.8rem; color:#94a3b8;">Case Status: <strong>${ticket.status === 'closed' ? 'Closed' : 'Active'}</strong></span>
            <button type="button" class="alphax-btn ${ticket.status === 'closed' ? 'alphax-btn-secondary' : 'alphax-btn-danger'}" id="btn-toggle-close-ticket">
              ${ticket.status === 'closed' ? 'Re-open Case' : 'Archive / Close Ticket'}
            </button>
          </div>
        </div>


        <!-- Staff Assignment & Handoff -->
        <div class="detail-section">
          <h4>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
            Assigned Staff & Reassignment
          </h4>
          <div class="reassign-box">
            <label style="font-size:0.8rem; color:#94a3b8;">Currently Assigned To:</label>
            <select id="select-reassign-staff" class="alphax-select" style="width:100%;">
              <option value="Unassigned" ${!ticket.assignedStaff || ticket.assignedStaff.name === 'Unassigned' ? 'selected' : ''}>-- Unassigned --</option>
              ${this.staffList.map(s => `
                <option value="${s.name}" ${ticket.assignedStaff && ticket.assignedStaff.name === s.name ? 'selected' : ''}>
                  ${s.name} (${s.role})
                </option>
              `).join('')}
            </select>

            <input type="text" id="input-reassign-reason" placeholder="Reason for reassignment / notes for next staff member..." class="alphax-select" style="width:100%; margin-top:0.4rem;" />
            <button type="button" class="alphax-btn alphax-btn-secondary" id="btn-submit-reassign" style="margin-top:0.4rem;">
              Confirm Staff Reassignment
            </button>


            ${ticket.assignmentHistory.length > 0 ? `
              <div class="reassign-history">
                <span style="font-size:0.75rem; color:#94a3b8; font-weight:600; text-transform:uppercase;">Assignment Audit Log:</span>
                ${ticket.assignmentHistory.map(h => `
                  <div class="history-item">
                    <strong>${h.assignedFrom}</strong> → <strong>${h.assignedTo}</strong>
                    <div style="color:#94a3b8; font-size:0.75rem;">${new Date(h.timestamp).toLocaleString()}</div>
                    <div style="margin-top:0.2rem; color:#cbd5e1;">"${h.reason}"</div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Package & RMA Shipping -->
        <div class="detail-section">
          <h4>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            RMA & Outbound Package Tracking
          </h4>
          <div style="display:flex; flex-direction:column; gap:0.75rem;">
            <div>
              <label style="font-size:0.8rem; color:#94a3b8; display:block; margin-bottom:0.2rem;">Incoming Carrier & Tracking #</label>
              <input type="text" id="input-incoming-tracking" value="${ticket.shipping.incoming ? ticket.shipping.incoming.trackingNumber : ''}" class="alphax-select" style="width:100%;" placeholder="e.g. UPS 1Z999..." />
            </div>

            <div>
              <label style="font-size:0.8rem; color:#94a3b8; display:block; margin-bottom:0.2rem;">Outbound Carrier & Tracking #</label>
              <input type="text" id="input-outgoing-tracking" value="${ticket.shipping.outgoing ? ticket.shipping.outgoing.trackingNumber : ''}" class="alphax-select" style="width:100%;" placeholder="e.g. FedEx Express 789012345678" />
            </div>

            <button type="button" class="alphax-btn alphax-btn-secondary" id="btn-save-tracking" style="align-self:flex-start;">
              Save Shipping Information
            </button>
          </div>
        </div>

          <!-- Media & Bench Photos -->
          <div class="detail-section">
            <h4>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              Photos & Bench Inspection Media
            </h4>

            <div class="photo-grid">
              ${ticket.attachments.map(att => {
                const previewSrc = getDirectImageUrl(att.url);
                return `<img src="${previewSrc}" alt="${att.name}" class="photo-thumb" onclick="window.open('${att.url}', '_blank')" />`;
              }).join('')}
            </div>

            <div class="dropzone-box" id="dropzone-photo">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-bottom:0.4rem;"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <div>Drop inspection photos here or click to upload</div>
              <input type="file" id="file-input-photo" accept="image/*" style="display:none;" />
            </div>
          </div>


        <!-- Comments & Internal Staff Notes -->
        <div class="detail-section">
          <h4>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Work Logs & Internal Comments
          </h4>

          <div class="comments-thread">
            ${ticket.comments.map(c => `
              <div class="comment-card">
                <div class="comment-header">
                  <span class="comment-author">${c.author}</span>
                  <span>${new Date(c.timestamp).toLocaleString()}</span>
                </div>
                <p class="comment-text">${c.text}</p>
              </div>
            `).join('')}
          </div>

          <div style="margin-top:1rem; display:flex; flex-direction:column; gap:0.5rem;">
            <textarea id="input-new-comment" rows="2" placeholder="Add bench observation or tuning notes..." class="alphax-select" style="width:100%;"></textarea>
            <button type="button" class="alphax-btn alphax-btn-primary" id="btn-add-comment" style="align-self:flex-end;">
              Post Staff Note
            </button>
          </div>
        </div>
      `;

      this.bindDrawerActions(ticket);
    }

    bindDrawerActions(ticket) {
      // Classification Toggles
      const toggleTuning = document.getElementById('toggle-tuning');
      const toggleHardware = document.getElementById('toggle-hardware');
      const toggleSale = document.getElementById('toggle-sale');

      if (toggleTuning) {
        toggleTuning.addEventListener('click', () => {
          ticket.classification = 'tuning';
          ticket.updatedAt = new Date().toISOString();
          this.saveTickets();
          this.render();
          this.openDrawer(ticket.id);
        });
      }

      if (toggleHardware) {
        toggleHardware.addEventListener('click', () => {
          ticket.classification = 'hardware';
          ticket.updatedAt = new Date().toISOString();
          this.saveTickets();
          this.render();
          this.openDrawer(ticket.id);
        });
      }

      if (toggleSale) {
        toggleSale.addEventListener('click', () => {
          ticket.classification = 'sale';
          if (ticket.status === 'received' || ticket.status === 'diagnosing') {
            ticket.status = 'preparing_ecu';
          }
          ticket.updatedAt = new Date().toISOString();
          this.saveTickets();
          this.render();
          this.openDrawer(ticket.id);
        });
      }


      // Save Status Dropdown
      const btnUpdateStatus = document.getElementById('btn-update-status');
      if (btnUpdateStatus) {
        btnUpdateStatus.addEventListener('click', () => {
          const selectStatus = document.getElementById('select-ticket-status');
          if (selectStatus) {
            ticket.status = selectStatus.value;
            ticket.updatedAt = new Date().toISOString();
            this.saveTickets();
            this.render();
            this.openDrawer(ticket.id);
          }
        });
      }

      // Archive / Close Ticket Toggle Button
      const btnToggleClose = document.getElementById('btn-toggle-close-ticket');
      if (btnToggleClose) {
        btnToggleClose.addEventListener('click', () => {
          const isClosed = ticket.status === 'closed';
          const author = this.currentUser ? this.currentUser.name : 'Staff';

          if (isClosed) {
            ticket.status = 'diagnosing';
            ticket.comments.push({
              id: "c_" + Date.now(),
              author: author,
              text: "Re-opened ticket case.",
              timestamp: new Date().toISOString()
            });
          } else {
            ticket.status = 'closed';
            ticket.comments.push({
              id: "c_" + Date.now(),
              author: author,
              text: "Closed & archived ticket case.",
              timestamp: new Date().toISOString()
            });

            // Remote Sync: Move ticket to 'Archived' tab and delete from active 'Tickets' tab in Google Sheets
            apiPost({
              action: 'archiveTicket',
              ticketId: ticket.id,
              archivedBy: author
            });
          }

          ticket.updatedAt = new Date().toISOString();
          this.saveTickets();
          this.render();
          this.closeDrawer();
        });
      }



      // Reassign Staff
      const btnReassign = document.getElementById('btn-submit-reassign');
      if (btnReassign) {
        btnReassign.addEventListener('click', () => {
          const selectStaff = document.getElementById('select-reassign-staff');
          const reasonInput = document.getElementById('input-reassign-reason');
          const newStaffName = selectStaff ? selectStaff.value : '';
          const reason = reasonInput ? reasonInput.value.trim() : '';

          if (!newStaffName) return;

          const currentStaff = ticket.assignedStaff ? ticket.assignedStaff.name : 'Unassigned';

          // When a user unassigns themselves from a ticket, and assigns a new person
          if (newStaffName !== currentStaff) {
            if (newStaffName === 'Unassigned') {
              ticket.assignedStaff = null;
              ticket.newAssignmentFor = null;
            } else {
              const staffObj = this.staffList.find(s => s.name === newStaffName);
              ticket.assignedStaff = {
                name: newStaffName,
                role: staffObj ? staffObj.role : (newStaffName.includes('Alex') ? 'Tuning Lead' : (newStaffName.includes('Dave') ? 'Hardware Tech' : 'Staff')),
                avatar: staffObj ? staffObj.avatar : newStaffName.split(' ').map(n => n[0]).join('')
              };
              // Set blue 1Hz glow flag for the newly assigned person
              ticket.newAssignmentFor = newStaffName;
            }

            ticket.assignmentHistory.push({
              assignedFrom: currentStaff,
              assignedTo: newStaffName,
              timestamp: new Date().toISOString(),
              reason: reason || 'Reassigned to next technician'
            });

            ticket.updatedAt = new Date().toISOString();

            this.saveTickets();
            this.render();

            // Display side toast on the upper right side
            if (newStaffName !== 'Unassigned') {
              this.showToast("you have a ticket update", `Ticket ${ticket.id} assigned to ${newStaffName}`, ticket.id);
            } else {
              this.showToast("you have a ticket update", `Ticket ${ticket.id} unassigned`, ticket.id);
            }

            this.openDrawer(ticket.id);

            // Remote Sync to Google Sheets ('Reassignments' tab & Email alert)
            apiPost({
              action: 'reassignStaff',
              ticketId: ticket.id,
              assignedFrom: currentStaff,
              assignedTo: newStaffName,
              reason: reason || 'Reassigned to next technician'
            });
          }
        });
      }

      // Save Tracking
      const btnTracking = document.getElementById('btn-save-tracking');
      if (btnTracking) {
        btnTracking.addEventListener('click', () => {
          const inc = document.getElementById('input-incoming-tracking').value;
          const out = document.getElementById('input-outgoing-tracking').value;

          if (!ticket.shipping) ticket.shipping = { incoming: {}, outgoing: {} };
          ticket.shipping.incoming.trackingNumber = inc;
          ticket.shipping.outgoing.trackingNumber = out;
          if (out) ticket.shipping.outgoing.status = 'Shipped / Label Created';

          ticket.updatedAt = new Date().toISOString();
          this.saveTickets();
          alert('Package tracking numbers saved successfully.');
          this.render();
        });
      }

      // Photo Dropzone
      const dropzone = document.getElementById('dropzone-photo');
      const fileInput = document.getElementById('file-input-photo');

      if (dropzone && fileInput) {
        dropzone.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', (e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
              const base64Data = event.target.result.split(',')[1];
              const tempAtt = {
                id: "img_" + Date.now(),
                name: file.name,
                url: event.target.result
              };

              ticket.attachments.push(tempAtt);
              ticket.updatedAt = new Date().toISOString();
              this.saveTickets();
              this.renderDrawerBody(ticket);

              // Remote Sync to Google Drive & Google Sheets ('Media' tab)
              const res = await apiPost({
                action: 'uploadPhoto',
                ticketId: ticket.id,
                fileName: file.name,
                fileType: file.type,
                base64Data: base64Data,
                uploadedBy: this.currentUser ? this.currentUser.name : 'Staff'
              });

              if (res && res.photoUrl) {
                tempAtt.url = res.photoUrl; // Replace local preview with Google Drive URL
                this.saveTickets();
                this.renderDrawerBody(ticket);
              }
            };
            reader.readAsDataURL(file);
          }
        });
      }


      // Post Comment
      const btnComment = document.getElementById('btn-add-comment');
      if (btnComment) {
        btnComment.addEventListener('click', () => {
          const input = document.getElementById('input-new-comment');
          const text = input ? input.value.trim() : '';
          if (!text) return;

          // Set author to the logged-in staff member's name
          const authorName = this.currentUser ? this.currentUser.name : (ticket.assignedStaff ? ticket.assignedStaff.name : 'Staff');

          const newComment = {
            id: "c_" + Date.now(),
            author: authorName,
            text: text,
            timestamp: new Date().toISOString()
          };

          ticket.comments.push(newComment);
          ticket.updatedAt = new Date().toISOString();
          this.saveTickets();
          this.renderDrawerBody(ticket);

          // Remote Sync to Google Sheets ('Comments' tab)
          apiPost({
            action: 'addComment',
            ticketId: ticket.id,
            author: authorName,
            text: text
          });
        });
      }

    }

    closeDrawer() {
      if (this.drawerBackdrop) {
        this.drawerBackdrop.classList.remove('active');
      }
      this.activeTicketId = null;
    }

    closeCPModal() {
      if (this.cpBackdrop) this.cpBackdrop.classList.remove('active');
      if (this.cpErrorMsg) this.cpErrorMsg.style.display = 'none';
    }

    openCreateModal() {

      if (this.createBackdrop) this.createBackdrop.classList.add('active');
    }

    closeCreateModal() {
      if (this.createBackdrop) this.createBackdrop.classList.remove('active');
    }

    handleCreateTicket(e) {
      e.preventDefault();
      const clientName = document.getElementById('new-client-name').value.trim();
      const orderId = document.getElementById('new-order-id').value.trim();
      const serialNumber = document.getElementById('new-serial-number').value.trim();
      const vehicle = document.getElementById('new-vehicle').value.trim();
      const classification = document.getElementById('new-classification').value;
      const assigneeName = document.getElementById('new-assignee').value;
      const incomingTracking = document.getElementById('new-incoming-tracking').value.trim();
      const issueDesc = document.getElementById('new-issue-desc').value.trim();

      const isSale = classification === 'sale';
      const newId = isSale ? `AX-SALE-${Math.floor(10000 + Math.random() * 90000)}` : `AX-TICK-${Math.floor(10000 + Math.random() * 90000)}`;

      const newTicket = {
        id: newId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        client: {
          name: clientName,
          orderId: orderId.startsWith('#') ? orderId : `#${orderId}`,
          serialNumber: serialNumber,
          vehicle: vehicle
        },
        classification: classification,
        status: isSale ? "preparing_ecu" : "received",
        priority: "normal",
        assignedStaff: {
          name: assigneeName,
          role: 'Staff',
          avatar: assigneeName.split(' ').map(n => n[0]).join('')
        },

        assignmentHistory: [],
        shipping: {
          incoming: { carrier: "Carrier", trackingNumber: incomingTracking, status: "Received" },
          outgoing: { carrier: "", trackingNumber: "", status: "Pending" }
        },
        attachments: [],
        comments: issueDesc ? [
          {
            id: "c_init",
            author: assigneeName,
            text: issueDesc,
            timestamp: new Date().toISOString()
          }
        ] : []
      };

      // 1. Save locally for instant UI update
      this.tickets.unshift(newTicket);
      this.saveTickets();
      this.closeCreateModal();
      this.formCreate.reset();
      this.render();

      // 2. Remote Sync to Google Sheets Database
      apiPost({
        action: 'createTicket',
        id: newTicket.id,
        clientName: newTicket.client.name,
        orderId: newTicket.client.orderId,
        serialNumber: newTicket.client.serialNumber,
        vehicle: newTicket.client.vehicle,
        classification: newTicket.classification,
        status: newTicket.status,
        assignedStaff: newTicket.assignedStaff ? newTicket.assignedStaff.name : '',
        incomingTracking: newTicket.shipping ? newTicket.shipping.incoming.trackingNumber : ''
      });
    }


    exportTicketsJSON() {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this.tickets, null, 2));
      const downloadAnchor = document.createElement('a');
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", `alphax_tickets_export_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
    }

    handlePublicClaim(e) {
      e.preventDefault();
      const form = e.target;
      const name = form.querySelector('[name="client_name"]').value;
      const orderId = form.querySelector('[name="order_id"]').value;
      const serialNumber = form.querySelector('[name="serial_number"]').value;
      const vehicle = form.querySelector('[name="vehicle"]').value;
      const desc = form.querySelector('[name="description"]').value;
      const tracking = form.querySelector('[name="incoming_tracking"]').value;

      const newId = `AX-TICK-${Math.floor(10000 + Math.random() * 90000)}`;

      const claimTicket = {
        id: newId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        client: { name, orderId, serialNumber, vehicle },
        classification: "diagnosing",
        status: "received",
        priority: "normal",
        assignedStaff: { name: "Sarah Chen", role: "Intake Specialist", avatar: "SC" },
        assignmentHistory: [],
        shipping: {
          incoming: { carrier: "Incoming Shipment", trackingNumber: tracking, status: "Submitted" },
          outgoing: { carrier: "", trackingNumber: "", status: "Pending" }
        },
        attachments: [],
        comments: [{ id: "c_pub", author: "Customer Submission", text: desc, timestamp: new Date().toISOString() }]
      };

      this.tickets.unshift(claimTicket);
      this.saveTickets();

      form.style.display = 'none';
      const successBox = document.getElementById('claim-success-message');
      const createdIdSpan = document.getElementById('claim-created-id');
      if (createdIdSpan) createdIdSpan.textContent = newId;
      if (successBox) successBox.style.display = 'block';
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.AlphaXAppInstance = new AlphaXApp();
  });
})();
